import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import {
  FormTemplate,
  FormTemplateSection,
} from 'data/template/template.types';
import * as types from 'data/filterGroup/filterGroup.types';
import Form, { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayout } from 'components/FormLayout';
import SectionLabel from 'components/SectionLabel';

type Props = {
  template: FormTemplate;
  data: Partial<types.FilterGroup>;
  i18nKeyPrefix?: string;
  isEditing?: boolean;
  onFieldUpdate?: (
    param: FieldUpdateHandlerParam,
    formSection?: 'main'
  ) => void;
};

export type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean;
  getValues: () => types.FilterGroup;
};

export const COLLECTION_FUNCTION_GROUP_I18N_KEY =
  'filterGroup.form';

export const FilterGroupForm = forwardRef<Ref, Props>(
  (
    {
      template,
      data,
      i18nKeyPrefix = COLLECTION_FUNCTION_GROUP_I18N_KEY,
      onFieldUpdate = () => null,
    }: Props,
    ref,
  ) => {
    const mainRef = useRef<Form>(null);

    const customFieldComponentMap = {
      privateKey: SectionLabel,
      certificate: SectionLabel,
    };

    const isValid = (forceValidation = false, forceTouched = true) => {
      const isMainValid =
        mainRef.current?.isValid(forceValidation, forceTouched) ?? true;
      return isMainValid;
    };

    const getValues = () => {
      const formValues: types.FilterGroup = {
        ...mainRef.current?.getValues(),
      };
      return formValues;
    };

    useImperativeHandle(ref, () => ({
      isValid,
      getValues,
    }));

    const onMainFieldUpdate = (param: FieldUpdateHandlerParam) =>
      onFieldUpdate(param, 'main');

    return (
      <div
        className="filter-group-form"
        data-testid="FilterGroupForm"
      >
        <div data-testid="FilterGroupFormMain">
          <FormLayout>
            <Form
              ref={mainRef}
              name={template.formId ?? template.key}
              defaults={data}
              fields={(template.main as FormTemplateSection).fields}
              layout={
                (template.main as FormTemplateSection).metadata.layout?.rows
              }
              i18nKeyPrefix={i18nKeyPrefix}
              fieldUpdateHandler={onMainFieldUpdate}
              customFieldComponentMap={customFieldComponentMap}
            />
          </FormLayout>
        </div>
      </div>
    );
  },
);

export default FilterGroupForm;
